import type { PracticalInfo, SocialLink } from './practicalInfo.js'
import { horairesEnSchemaOrg, type SpecificationHoraire } from './horaires.js'
import { siteCanonique } from './siteRoutes.js'

/**
 * Le balisage Schema.org de l'établissement (issue #146).
 *
 * L'audit SEOptimer n'en détectait aucun. Pour un institut avec une adresse
 * physique, c'est le manque le plus rentable qu'il ait relevé : c'est ce
 * balisage qui permet aux moteurs de rattacher le site à un lieu réel, et aux
 * moteurs génératifs de répondre avec assurance à « spa à Auxerre ».
 *
 * La fonction est ici, dans les contrats, et non dans le site : elle ne fait
 * que dériver des données déjà décrites par `practicalInfoSchema`, et c'est
 * ce que font déjà `catalogDerive`, `mediaDerive` et leurs voisins. Elle est
 * pure, donc testable sans monter de composant.
 *
 * LES HORAIRES n'y figuraient pas à la livraison : les seuls disponibles
 * étaient trois lignes de texte libre — « Ouvert 5/7 jours », « Lundi -
 * Mardi - … » — qui ne portent aucune heure, quand
 * `openingHoursSpecification` en exige deux. Ils y entrent depuis l'issue
 * #158, mais seulement quand de vraies plages sont saisies : sans elles, la
 * propriété disparaît du JSON plutôt que d'annoncer des heures devinées, qui
 * feraient afficher « Ouvert » par Google la porte close.
 *
 * CE QU'ELLE NE PRODUIT TOUJOURS PAS, et pourquoi :
 *
 * - **Les coordonnées géographiques.** Aucune latitude ni longitude n'existe
 *   dans les données du site. Google géocode l'adresse ; des coordonnées
 *   inventées le tromperaient.
 * - **La fourchette de prix.** `priceRange` n'est nulle part, et un « €€ »
 *   posé au jugé engagerait l'institut sur ce qu'il n'a pas dit.
 */

const NOM = 'Azoya Spa Vaux'
const DESCRIPTION =
  'Institut de beauté et spa à quelques minutes d’Auxerre : soins du visage, ' +
  'massages, épilation, beauté des mains et des pieds.'

export interface AdressePostale {
  '@type': 'PostalAddress'
  streetAddress: string
  postalCode: string
  addressLocality: string
  addressCountry: 'FR'
}

export interface SchemaEtablissement {
  '@context': 'https://schema.org'
  '@type': 'DaySpa'
  name: string
  description: string
  url: string
  logo: string
  image: string
  /** Découpée quand son format s'y prête, laissée en bloc sinon. */
  address: AdressePostale | string
  telephone: string
  email: string
  hasMap: string
  areaServed: string
  /** Absent plutôt que vide : aucun profil vaut mieux qu'un profil faux. */
  sameAs?: string[]
  /**
   * Renseigné dès que de vraies plages sont saisies (issue #158), absent tant
   * qu'il n'y en a pas — et non pas rempli d'heures devinées, qui feraient
   * afficher « Ouvert » quand l'institut est fermé.
   */
  openingHoursSpecification?: SpecificationHoraire[]
  /** Jamais renseignés — voir l'en-tête du fichier. */
  geo?: never
}

/**
 * « 1bis, Rue Saint-Vincent 89290 Auxerre » → ses trois composants.
 *
 * Le code postal sert de repère : cinq chiffres isolés, ce qui tient tant que
 * l'adresse reste française. Ce qui précède est la voie, ce qui suit la
 * commune. `null` quand le repère manque — l'appelant donne alors l'adresse
 * telle quelle plutôt qu'un découpage inventé.
 */
function decouperAdresse(adresse: string): AdressePostale | null {
  const correspondance = adresse.match(/^(.+?)[\s,]+(\d{5})\s+(.+)$/)
  if (!correspondance) return null

  const [, voie, codePostal, commune] = correspondance
  return {
    '@type': 'PostalAddress',
    streetAddress: voie.trim().replace(/,$/, ''),
    postalCode: codePostal,
    addressLocality: commune.trim(),
    addressCountry: 'FR',
  }
}

/**
 * « 06 79 33 33 64 » → « +33679333364 ».
 *
 * Un numéro national ne se lit sans ambiguïté que par un lecteur qui connaît
 * déjà le pays. Un numéro déjà international est seulement débarrassé de ses
 * espaces.
 */
function telephoneInternational(numero: string): string {
  const compact = numero.replace(/[\s.\-()]/g, '')
  if (compact.startsWith('+')) return compact
  if (compact.startsWith('0')) return `+33${compact.slice(1)}`
  return compact
}

/**
 * Une adresse ne désignant qu'une racine de domaine n'est pas un profil.
 *
 * Le repli embarqué de `practicalInfo` porte « https://www.instagram.com/ »
 * en attendant les vraies adresses. Reprise dans `sameAs`, elle déclarerait
 * aux moteurs que le compte officiel de l'institut est Instagram lui-même.
 */
function designeUnProfil(lien: SocialLink): boolean {
  try {
    const { pathname, search } = new URL(lien.url)
    return pathname.replace(/\/+$/, '').length > 0 || search.length > 0
  } catch {
    return false
  }
}

export function schemaDeLEtablissement(info: PracticalInfo): SchemaEtablissement {
  const profils = info.socialLinks.filter(designeUnProfil).map((lien) => lien.url)
  const adresseDecoupee = decouperAdresse(info.address)
  const horaires = horairesEnSchemaOrg(info.openingHours)

  return {
    '@context': 'https://schema.org',
    '@type': 'DaySpa',
    name: NOM,
    description: DESCRIPTION,
    url: `${siteCanonique}/`,
    logo: `${siteCanonique}/android-chrome-256x256.png`,
    image: `${siteCanonique}/partage-azoya-spa.jpg`,
    address: adresseDecoupee ?? info.address,
    telephone: telephoneInternational(info.phone),
    email: info.email,
    hasMap: info.mapsUrl,
    areaServed: 'Auxerre',
    // `undefined` et non un tableau vide : la clé disparaît alors du JSON,
    // au lieu d'annoncer une liste de profils qui n'en contient aucun.
    sameAs: profils.length > 0 ? profils : undefined,
    // Même raison : sans plage saisie, la clé disparaît plutôt que d'annoncer
    // un établissement dont aucune heure d'ouverture n'est connue.
    openingHoursSpecification: horaires.length > 0 ? horaires : undefined,
  }
}
